import React, { useEffect, useState } from 'react'
import { Select, Button } from 'antd'
import {
  RawIdolProductionReport,
  ColoringProductionReport,
  GetProductDataAPI,
  RawProductionReportYearwise,
  ColoringProductReportYearwise,
} from '../../AllAPI/AdminAPI'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import * as XLSX from 'xlsx'
import { SlackOutlined } from '@ant-design/icons'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import NotoFont from '../../Assets/Font/NotoSansDevanagari'
import axiosInstance from '../../Utils/axiosInstance'

const { Option } = Select

const ProductionReport = () => {
  const selectedYear = Number(sessionStorage.getItem('year'))

  const [loading, setLoading] = useState(false)
  const [productList, setProductList] = useState([])
  const [viewType, setViewType] = useState('excel')
  const [optionType, setOptionType] = useState('all')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [reportType, setReportType] = useState('raw')
  const [pdfData, setPdfData] = useState([])

  /* ---------------- Load Products ---------------- */
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axiosInstance.get(`${GetProductDataAPI}/${selectedYear}`)
        setProductList(res.data || [])
      } catch (err) {
        console.error('Product fetch error:', err)
      }
    }
    fetchProducts()
  }, [selectedYear])

  const formatDate = (dateString) => {
    if (!dateString) return ''
    return dayjs(dateString).format('DD-MM-YYYY')
  }

  /* ---------------- Handle Show ---------------- */
  const handleShow = async () => {
    try {
      setLoading(true)

      let url = ''

      if (reportType === 'raw') {
        url =
          optionType === 'all'
            ? `${RawProductionReportYearwise}/${selectedYear}`
            : `${RawIdolProductionReport}/${selectedProduct}/${selectedYear}`
      } else {
        url =
          optionType === 'all'
            ? `${ColoringProductReportYearwise}/${selectedYear}`
            : `${ColoringProductionReport}/${selectedProduct}/${selectedYear}`
      }

      const res = await axiosInstance.get(url)
      const apiData = res.data || []

      if (!apiData.length) {
        toast.warning('No data found for selected filters')
        return
      }

      if (viewType === 'excel') {
        generateExcel(apiData)
      } else {
        setPdfData(apiData)
        generatePDF(apiData)
      }
    } catch (err) {
      console.error('Report error:', err)
    } finally {
      setLoading(false)
    }
  }

  /* ---------------- Excel ---------------- */
  const generateExcel = (data, fileName = 'Production_Report') => {
    const formattedData = data.map((item) => ({
      Date:
        reportType === 'raw'
          ? formatDate(item.raw_idol_production_date)
          : formatDate(item.coloring_date),
      'Product Name': item.product_name,
      Size: item.size,
      'Short Name': item.short_name,
      Quantity:
        reportType === 'raw'
          ? item.raw_idol_production_quantity
          : item.coloring_quantity,
    }))

    const totalQty = formattedData.reduce(
      (sum, item) => sum + Number(item.Quantity || 0),
      0,
    )

    formattedData.push({
      Date: '',
      'Product Name': '',
      Size: '',
      'Short Name': 'Total',
      Quantity: totalQty,
    })

    const worksheet = XLSX.utils.json_to_sheet(formattedData)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Production Report')

    //  Generate Current Date (DD-MM-YYYY)
    const today = new Date()
    const formattedDate = `${String(today.getDate()).padStart(2, '0')}-${String(
      today.getMonth() + 1,
    ).padStart(2, '0')}-${today.getFullYear()}`

    const finalFileName = `${fileName} (${formattedDate}).xlsx`

    //  Download file
    XLSX.writeFile(workbook, finalFileName)
  }

  /* ================= PDF (AutoTable) ================= */
  const generatePDF = (data) => {
    const doc = new jsPDF('p', 'mm', 'a4')

    // Marathi Font
    doc.addFileToVFS('NotoSansDevanagari.ttf', NotoFont)
    doc.addFont('NotoSansDevanagari.ttf', 'NotoSans', 'normal')
    doc.setFont('NotoSans')

    const tableColumns = ['Date', 'Product Name', 'Size', 'Short Name', 'Qty']

    const tableRows = data.map((item) => [
      reportType === 'raw'
        ? formatDate(item.raw_idol_production_date)
        : formatDate(item.coloring_date),
      item.product_name,
      item.size,
      item.short_name,
      reportType === 'raw'
        ? item.raw_idol_production_quantity
        : item.coloring_quantity,
    ])

    const totalQty = data.reduce(
      (sum, item) =>
        sum +
        Number(
          reportType === 'raw'
            ? item.raw_idol_production_quantity
            : item.coloring_quantity,
        ),
      0,
    )

    tableRows.push(['', '', '', 'Total', totalQty])

    doc.setFontSize(14)
    doc.text(
      reportType === 'raw'
        ? 'RAW IDOL PRODUCTION REPORT'
        : 'COLORING PRODUCTION REPORT',
      14,
      15,
    )

    const today = new Date().toLocaleDateString('en-GB')

    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 22,
      theme: 'grid',

      //  Make table full width
      tableWidth: 'auto',

      styles: {
        font: 'NotoSans',
        fontSize: 8,
        cellPadding: 2,
      },

      headStyles: {
        fillColor: [255, 102, 0],
      },

      margin: { left: 10, right: 10 },

      didDrawPage: function () {
        const pageCount = doc.internal.getNumberOfPages()
        const pageWidth = doc.internal.pageSize.getWidth()
        const pageHeight = doc.internal.pageSize.getHeight()

        doc.setFontSize(9)

        // Page number
        doc.text(
          `Page ${doc.internal.getCurrentPageInfo().pageNumber}`,
          pageWidth - 45,
          pageHeight - 8,
        )

        // Generated date
        doc.text(`(${today})`, pageWidth - 10, pageHeight - 8, {
          align: 'right',
        })
      },
    })

    const blob = doc.output('blob')
    window.open(URL.createObjectURL(blob), '_blank')
  }

  return (
    <div className="mainContainer">
      {loading && (
        <div className="fullscreen-loader">
          <div className="loader-box">
            <div className="spinner"></div>
            <p>Generating Report...</p>
          </div>
        </div>
      )}

      <div className="header-raw">
        <h3>
          <SlackOutlined style={{ color: 'orangered' }} /> Production Report
        </h3>
      </div>

      <div className="row p-2 m-0 mb-2 rounded border shadow">
        <div className="col-md-3 pt-2">
          <label>View Type</label>
          <Select
            style={{ width: '100%' }}
            value={viewType}
            onChange={(value) => setViewType(value)}
          >
            <Option value="excel">Excel</Option>
            <Option value="pdf">PDF</Option>
          </Select>
        </div>

        <div className="col-md-3 pt-2">
          <label>Options</label>
          <Select
            style={{ width: '100%' }}
            value={optionType}
            onChange={(value) => setOptionType(value)}
          >
            <Option value="all">All</Option>
            <Option value="product">Product Wise</Option>
          </Select>
        </div>

        {optionType === 'product' && (
          <div className="col-md-3 pt-2">
            <label>Product</label>
            <Select
              style={{ width: '100%' }}
              value={selectedProduct}
              onChange={(value) => setSelectedProduct(value)}
            >
              {productList.map((p) => (
                <Option key={p.prod_id} value={p.prod_id}>
                  {p.product_name} ({p.size})
                </Option>
              ))}
            </Select>
          </div>
        )}

        <div className="col-md-3 pt-2">
          <label>Product Type</label>
          <Select
            style={{ width: '100%' }}
            value={reportType}
            onChange={(value) => setReportType(value)}
          >
            <Option value="raw">Raw Idol Product</Option>
            <Option value="coloring">Coloring Product</Option>
          </Select>
        </div>

        <div className="col-md-3 pt-2 d-flex align-items-end">
          <Button
            type="primary"
            className="rounded"
            loading={loading}
            onClick={handleShow}
          >
            Show
          </Button>
        </div>
      </div>
    </div>
  )
}

export default ProductionReport
